import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import logger from '../utils/logger';

interface SettingsProps {
  provider: 'openai' | 'ollama';
  setProvider: (provider: 'openai' | 'ollama') => void;
  ollamaModel: string;
  setOllamaModel: (model: string) => void;
  openaiModel: string;
  setOpenaiModel: (model: string) => void;
  useQueryEmbedding: boolean;
  setUseQueryEmbedding: (value: boolean) => void;
  useCache: boolean;
  setUseCache: (value: boolean) => void;
  useMemory: boolean;
  setUseMemory: (value: boolean) => void;
}

const Settings: React.FC<SettingsProps> = ({ provider, setProvider, ollamaModel, setOllamaModel, openaiModel, setOpenaiModel, useQueryEmbedding, setUseQueryEmbedding, useCache, setUseCache, useMemory, setUseMemory }) => {
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [customGptModel, setCustomGptModel] = useState(openaiModel);
  const [validatingModel, setValidatingModel] = useState(false);
  const [modelValidationMessage, setModelValidationMessage] = useState<{ type: 'success' | 'error' | ''; message: string }>({ type: '', message: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  // Save settings to database when any setting changes
  const saveSettingsToDatabase = async (newProvider: 'openai' | 'ollama', newUseQueryEmbedding: boolean, newUseCache: boolean, newUseMemory: boolean) => {
    setSavingSettings(true);
    try {
      await apiService.updateSettings(newProvider, newUseQueryEmbedding, newUseCache, newUseMemory);
      logger.info(`Settings saved to database: provider=${newProvider}, useQueryEmbedding=${newUseQueryEmbedding}, useCache=${newUseCache}, useMemory=${newUseMemory}`);
    } catch (error) {
      logger.error('Failed to save settings to database:', error);
    } finally {
      setSavingSettings(false);
    }
  };

  // Handle provider change
  const handleProviderChange = async (newProvider: 'openai' | 'ollama') => {
    setProvider(newProvider);
    await saveSettingsToDatabase(newProvider, useQueryEmbedding, useCache, useMemory);
  };

  // Handle query embedding toggle
  const handleQueryEmbeddingChange = async (newValue: boolean) => {
    setUseQueryEmbedding(newValue);
    await saveSettingsToDatabase(provider, newValue, useCache, useMemory);
  };

  // Handle cache toggle
  const handleCacheChange = async (newValue: boolean) => {
    setUseCache(newValue);
    await saveSettingsToDatabase(provider, useQueryEmbedding, newValue, useMemory);
  };

  // Handle memory toggle
  const handleMemoryChange = async (newValue: boolean) => {
    setUseMemory(newValue);
    await saveSettingsToDatabase(provider, useQueryEmbedding, useCache, newValue);
  };

  // Fetch available Ollama models when component mounts or when provider changes to ollama
  useEffect(() => {
    const fetchModels = async () => {
      setLoadingModels(true);
      try {
        const models = await apiService.getOllamaModels();
        setAvailableModels(models);
        logger.info('Available Ollama models:', models);
        
        // If current model is not in the list and there are models available, select the first one
        if (models.length > 0 && !models.includes(ollamaModel)) {
          setOllamaModel(models[0]);
        }
      } catch (error) {
        logger.error('Failed to fetch Ollama models:', error);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchModels();
  }, [ollamaModel, setOllamaModel]);

  // Sync customGptModel with openaiModel prop
  useEffect(() => {
    setCustomGptModel(openaiModel);
  }, [openaiModel]);

  const handleValidateAndSaveModel = async () => {
    const trimmedModel = customGptModel.trim();
    
    if (!trimmedModel) {
      setModelValidationMessage({ type: 'error', message: 'Please enter a model name' });
      return;
    }

    setValidatingModel(true);
    setModelValidationMessage({ type: '', message: '' });

    try {
      const result = await apiService.validateOpenAIModel(trimmedModel);
      
      if (result.valid) {
        setOpenaiModel(trimmedModel);
        setModelValidationMessage({ 
          type: 'success', 
          message: `✓ Model "${trimmedModel}" is valid and has been saved!` 
        });
        logger.info(`Model ${trimmedModel} validated and saved`);
      } else {
        setModelValidationMessage({ 
          type: 'error', 
          message: result.message || 'Model does not exist or is not accessible' 
        });
      }
    } catch (error) {
      logger.error('Error validating model:', error);
      setModelValidationMessage({ 
        type: 'error', 
        message: 'Failed to validate model. Please try again.' 
      });
    } finally {
      setValidatingModel(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Settings
        </h2>

        <div className="space-y-6">
          {/* AI Provider Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              AI Provider
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select the AI provider to use for intent classification across all features.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="space-y-4">
                {/* OpenAI Option */}
                <div
                  onClick={() => handleProviderChange('openai')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    provider === 'openai'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          provider === 'openai'
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}
                      >
                        {provider === 'openai' && (
                          <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <h4 className="text-base font-semibold text-gray-900">
                          OpenAI
                        </h4>
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          Recommended
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        Use OpenAI's GPT models for high-quality intent classification. Supports both text and voice classification.
                      </p>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Text Classification
                        </span>
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Voice Classification
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ollama Option */}
                <div
                  onClick={() => handleProviderChange('ollama')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    provider === 'ollama'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          provider === 'ollama'
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}
                      >
                        {provider === 'ollama' && (
                          <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <h4 className="text-base font-semibold text-gray-900">
                          Ollama
                        </h4>
                        <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          Local
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        Use Ollama for local, self-hosted AI models. Privacy-focused and cost-effective.
                      </p>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Text Classification
                        </span>
                        <span className="flex items-center text-gray-400">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Voice Classification (Not Available)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* OpenAI Model Selection */}
            {provider === 'openai' && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  GPT Model Version
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  Specify which GPT model to use. Common models: gpt-4o-mini, gpt-4o, gpt-4-turbo, gpt-3.5-turbo
                </p>
                
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={customGptModel}
                      onChange={(e) => setCustomGptModel(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleValidateAndSaveModel();
                        }
                      }}
                      placeholder="e.g., gpt-4o-mini"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={validatingModel}
                    />
                    <button
                      onClick={handleValidateAndSaveModel}
                      disabled={validatingModel}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {validatingModel ? (
                        <div className="flex items-center">
                          <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="80" strokeDashoffset="60"></circle>
                          </svg>
                          Validating...
                        </div>
                      ) : (
                        'Validate & Save'
                      )}
                    </button>
                  </div>
                  
                  {modelValidationMessage.message && (
                    <div className={`p-3 rounded-md ${
                      modelValidationMessage.type === 'success' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className={`text-sm ${
                        modelValidationMessage.type === 'success' 
                          ? 'text-green-800' 
                          : 'text-red-800'
                      }`}>
                        {modelValidationMessage.message}
                      </p>
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    <p className="font-medium mb-1">Current model: <span className="font-mono text-gray-700">{openaiModel}</span></p>
                    <p>Type a model name and click "Validate & Save" to change it.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Ollama Model Selection */}
            {provider === 'ollama' && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  Select Ollama Model
                </h4>
                
                {loadingModels ? (
                  <div className="flex items-center justify-center py-4">
                    <svg className="animate-spin h-5 w-5 text-blue-600 mr-2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="80" strokeDashoffset="60"></circle>
                    </svg>
                    <span className="text-sm text-gray-600">Loading available models...</span>
                  </div>
                ) : availableModels.length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          No Ollama models found
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>Please install Ollama and pull a model:</p>
                          <code className="block mt-2 p-2 bg-yellow-100 rounded text-xs">
                            ollama pull llama3.2
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <select
                      value={ollamaModel}
                      onChange={(e) => setOllamaModel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {availableModels.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-gray-500">
                      {availableModels.length} model{availableModels.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Info Message */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800">
                    Current Selection: {provider === 'openai' ? 'OpenAI' : 'Ollama'}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      This setting applies to all classification features. You can switch providers at any time.
                      {provider === 'ollama' && ' Note: Voice classification will fall back to OpenAI when using Ollama.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Query Embedding Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Classification Options
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure how text classification performs similarity search.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="query-embedding"
                    type="checkbox"
                    checked={useQueryEmbedding}
                    onChange={(e) => handleQueryEmbeddingChange(e.target.checked)}
                    disabled={savingSettings}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="query-embedding" className="font-medium text-gray-900">
                    Enable Query Embedding
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Uses semantic similarity search to find the most relevant action-object pairs before classification. 
                    Improves accuracy but adds processing time (~1 second).
                  </p>
                  <div className="mt-3 text-xs text-gray-500">
                    <p className="font-medium mb-1">
                      Status: <span className={useQueryEmbedding ? 'text-green-600' : 'text-orange-600'}>
                        {useQueryEmbedding ? '✓ Enabled (Recommended)' : '✗ Disabled'}
                      </span>
                    </p>
                    {!useQueryEmbedding && (
                      <p className="text-orange-600">
                        ⚠️ Disabling this may reduce classification accuracy for complex queries.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cache Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Cache Settings
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure whether classification cache should be used.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="use-cache"
                    type="checkbox"
                    checked={useCache}
                    onChange={(e) => handleCacheChange(e.target.checked)}
                    disabled={savingSettings}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="use-cache" className="font-medium text-gray-900">
                    Enable Classification Cache
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    When enabled, the system will cache classification results to improve performance and reduce API costs. 
                    Previously classified inputs will be retrieved from cache instead of re-processing.
                  </p>
                  <div className="mt-3 text-xs text-gray-500">
                    <p className="font-medium mb-1">
                      Status: <span className={useCache ? 'text-green-600' : 'text-orange-600'}>
                        {useCache ? '✓ Enabled (Recommended)' : '✗ Disabled'}
                      </span>
                    </p>
                    {!useCache && (
                      <p className="text-orange-600">
                        ⚠️ Disabling cache will increase API usage and costs as every request will be processed fresh.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Memory Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              User Memory
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure whether past user interactions are remembered and used to personalise future classifications.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="use-memory"
                    type="checkbox"
                    checked={useMemory}
                    onChange={(e) => handleMemoryChange(e.target.checked)}
                    disabled={savingSettings}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="use-memory" className="font-medium text-gray-900">
                    Enable User Memory
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    When enabled, the system stores each user's confirmed intent history and uses it as personalised context
                    in future classifications. Disable to treat every session as fresh with no prior knowledge.
                  </p>
                  <div className="mt-3 text-xs text-gray-500">
                    <p className="font-medium mb-1">
                      Status: <span className={useMemory ? 'text-green-600' : 'text-orange-600'}>
                        {useMemory ? '✓ Enabled (Recommended)' : '✗ Disabled'}
                      </span>
                    </p>
                    {!useMemory && (
                      <p className="text-orange-600">
                        ⚠️ Disabling memory means the system will not learn from past interactions and personalisation will be lost.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;

